// src/privacy/cookies/CookieConsent.tsx

import React, { useEffect, useMemo, useState } from "react";
import "./cookie-consent.css";
import {
  defaultConsent,
  getStoredConsent,
  storeConsent,
  CookieConsentState,
} from "./cookieConsent.state";

type Props = {
  privacyPolicyPath?: string; // e.g. "/privacy"
  cookiePolicyPath?: string;  // e.g. "/cookies"
};

export const CookieConsent: React.FC<Props> = ({
  privacyPolicyPath = "/privacy",
  cookiePolicyPath = "/cookies",
}) => {
  const existing = useMemo(() => getStoredConsent(), []);
  const [visible, setVisible] = useState(!existing);
  const [expanded, setExpanded] = useState(false);

  const [analytics, setAnalytics] = useState(existing?.analytics ?? false);
  const [marketing, setMarketing] = useState(existing?.marketing ?? false);

  useEffect(() => {
    // If consent already exists, no banner
    if (existing) setVisible(false);
  }, [existing]);

  if (!visible) return null;

  const acceptAll = () => {
    const consent: CookieConsentState = {
      ...defaultConsent,
      analytics: true,
      marketing: true,
    };
    storeConsent(consent);
    setVisible(false);
  };

  const rejectAll = () => {
    const consent: CookieConsentState = {
      ...defaultConsent,
      analytics: false,
      marketing: false,
    };
    storeConsent(consent);
    setVisible(false);
  };

  const savePreferences = () => {
    const consent: CookieConsentState = {
      ...defaultConsent,
      analytics,
      marketing,
    };
    storeConsent(consent);
    setVisible(false);
  };

  return (
    <div className="cc-backdrop" role="dialog" aria-modal="true" aria-label="Cookie preferences">
      <div className="cc-card">
        <div className="cc-header">
          <div>
            <h3 className="cc-title">Cookies & Privacy</h3>
            <p className="cc-subtitle">
              We use essential cookies to make this site work. With your permission, we may also use
              analytics and marketing cookies to improve your experience.
            </p>
          </div>
        </div>

        <div className="cc-actions">
          <button type="button" className="cc-btn cc-btn--secondary" onClick={rejectAll}>
            Reject non-essential
          </button>
          <button type="button" className="cc-btn cc-btn--primary" onClick={acceptAll}>
            Accept all
          </button>
        </div>

        <div className="cc-links">
          <button type="button" className="cc-link" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Hide settings" : "Manage settings"}
          </button>
          <span className="cc-dot">•</span>
          <a className="cc-link" href={privacyPolicyPath}>
            Privacy Policy
          </a>
          <span className="cc-dot">•</span>
          <a className="cc-link" href={cookiePolicyPath}>
            Cookie Policy
          </a>
        </div>

        {expanded && (
          <div className="cc-prefs">
            <div className="cc-pref">
              <div className="cc-pref__text">
                <div className="cc-pref__title">Essential</div>
                <div className="cc-pref__desc">
                  Required for security and core functionality. Always enabled.
                </div>
              </div>
              <div className="cc-toggle cc-toggle--locked">On</div>
            </div>

            <div className="cc-pref">
              <div className="cc-pref__text">
                <div className="cc-pref__title">Analytics</div>
                <div className="cc-pref__desc">
                  Helps us understand usage and improve the product.
                </div>
              </div>
              <label className="cc-switch">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                />
                <span className="cc-slider" />
              </label>
            </div>

            <div className="cc-pref">
              <div className="cc-pref__text">
                <div className="cc-pref__title">Marketing</div>
                <div className="cc-pref__desc">
                  Used for personalised ads and measuring campaigns.
                </div>
              </div>
              <label className="cc-switch">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                />
                <span className="cc-slider" />
              </label>
            </div>

            <div className="cc-prefs__footer">
              <button type="button" className="cc-btn cc-btn--ghost" onClick={() => setExpanded(false)}>
                Cancel
              </button>
              <button type="button" className="cc-btn cc-btn--primary" onClick={savePreferences}>
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
